import React, { useState, useCallback, useContext, useEffect } from 'react';
import { View, TouchableOpacity, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useQuery, useMutation } from 'react-apollo';
import { MeshIcon, Divider, ListItem } from '../common-ui';
import { Colors, Typography } from '../common-styles';
import { AppContext } from '../../AppContext';
import { SETTING_QUERY, UPDATE_SETTING_MUTATION } from '../graphql';
import { DrawerSettingNavigatorScreens } from '../common-types/navigation-types';

type DrawerNavigation = StackNavigationProp<DrawerSettingNavigatorScreens, `DrawerSettingsMenu`>;

export const VisibilityManage = () => {
  const navigation = useNavigation<DrawerNavigation>();
  const navigateToVisibilitySettings = useCallback(() => navigation.push(`VisibilitySettings`), [navigation]);

  return (
    <View>
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={navigateToVisibilitySettings}
        style={{ flex: 1, flexDirection: `row`, justifyContent: `space-between`, backgroundColor: Colors.white, padding: 16 }}
      >
        <View style={{ flex: 1, justifyContent: `center` }}>
          <Text style={Typography.text(`plusone`, `bold`)}>Profile visibility</Text>
        </View>
        <MeshIcon name="chevron-right" color={Colors.iconColor} />
      </TouchableOpacity>
      <Divider />
    </View>
  );
};

export const VisibilitySettings = () => {
  const { user } = useContext(AppContext);
  const { data, loading } = useQuery(SETTING_QUERY, {
    variables: {
      settingQuery: {
        query: `user.setting.visibility`,
        user_id: user?.id,
      },
    },
    fetchPolicy: `network-only`,
  });
  const [settingValue, setSettingValue] = useState(``);

  const [updateSettingMutation] = useMutation(UPDATE_SETTING_MUTATION);
  useEffect(() => {
    setSettingValue(data?.getSettings?.[0]?.value);
  }, [data]);

  const updateSetting = useCallback(
    async (_value) => {
      if (settingValue !== _value) {
        const value = _value;
        try {
          await updateSettingMutation({
            variables: {
              input: {
                key: `user.setting.visibility`,
                value,
                user_id: user?.id,
              },
            },
          });
          setSettingValue(_value);
        } catch (error) {
          console.error(error);
        }
      }
    },
    [settingValue, updateSettingMutation, user?.id],
  );

  return (
    <View>
      <Text style={localStyles.headerTitle}>Decide who can view your profile</Text>
      <View style={localStyles.headerContent}>
        <Text style={{ ...Typography.text(`base`, `bold`, `gray`) }}>Visibility</Text>
      </View>
      {loading ? (
        <ActivityIndicator />
      ) : (
        <>
          <ListItem
            title="Public"
            titleStyle={localStyles.listItemTitle}
            subtitle="Anyone can view your public posts."
            subtitleStyle={localStyles.subTitleStyle}
            rightIcon={
              <MeshIcon
                name="select-circle"
                color={settingValue === `public` ? Colors.brandPurple : Colors.gray}
                focused={settingValue === `public`}
              />
            }
            onPress={() => {
              updateSetting(`public`);
            }}
          />
          <Divider />
          <ListItem
            title="Private"
            titleStyle={localStyles.listItemTitle}
            subtitle="Only your followers can view your public posts."
            subtitleStyle={localStyles.subTitleStyle}
            rightIcon={
              <MeshIcon
                name="select-circle"
                color={settingValue === `private` ? Colors.brandPurple : Colors.gray}
                focused={settingValue === `private`}
              />
            }
            onPress={() => {
              updateSetting(`private`);
            }}
          />
          <Divider />
          <Text style={[localStyles.headerTitle, { paddingHorizontal: 40 }]}>
            Posts that are in a node with Private content are only shared with other members of that node.
          </Text>
        </>
      )}
    </View>
  );
};

const localStyles = StyleSheet.create({
  headerTitle: {
    paddingVertical: 16,
    textAlign: `center`,
    fontSize: 12,
  },
  headerContent: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    backgroundColor: Colors.backgroundColor,
  },
  listItemTitle: {
    fontFamily: `inter-semibold`,
    fontSize: Typography.baseFontSize,
    color: Colors.textBlack,
    fontWeight: `500`,
  },
  subTitleStyle: {
    fontFamily: `inter-semibold`,
    fontSize: 12,
    color: Colors.gray,
    paddingVertical: 5,
    width: `85%`,
  },
});
